import React from 'react';
import { Progress, Tag, Tooltip } from 'antd';

const LEVEL_COLORS = {
  green: '#52c41a',
  yellow: '#faad14',
  orange: '#fa8c16',
  red: '#ff4d4f',
};

const LEVEL_LABELS = {
  green: '正常',
  yellow: '预警',
  orange: '暂停',
  red: '硬停',
};

const ZERO_USAGE = {
  peak_pct: 0,
  call_pct: 0,
  cpu_pct: 0,
  time_pct: 0,
  level: 'green',
};

function levelKey(level) {
  return String(level || 'green').toLowerCase();
}

function pctColor(pct, thresholds) {
  const v = Number(pct) || 0;
  const t = thresholds || { warn: 60, pause: 80, hard_stop: 95 };
  if (v >= t.hard_stop) return LEVEL_COLORS.red;
  if (v >= t.pause) return LEVEL_COLORS.orange;
  if (v >= t.warn) return LEVEL_COLORS.yellow;
  return LEVEL_COLORS.green;
}

function MiniBar({ label, pct, thresholds }) {
  const value = Math.min(100, Math.max(0, Number(pct) || 0));
  return (
    <div style={{ marginBottom: 4 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: '#666' }}>
        <span>{label}</span>
        <span>{value.toFixed(1)}%</span>
      </div>
      <Progress
        percent={value}
        showInfo={false}
        strokeColor={pctColor(value, thresholds)}
        size="small"
      />
    </div>
  );
}

function appGatePeak(data) {
  const call = Number(data?.call_pct) || 0;
  const cpu = Number(data?.cpu_pct) || 0;
  return Math.max(call, cpu);
}

function buildUsageTip(data, { appMode = false } = {}) {
  if (!data.updated_at) {
    return appMode
      ? '尚未收到 X-App-Usage 响应头；应用暂停仅依据 Call/CPU，当前显示 0'
      : '尚未收到 Meta API 响应头，显示 0';
  }
  const updated = `更新: ${new Date(data.updated_at).toLocaleString()}`;
  if (appMode) {
    return `${updated}。应用级峰值 = max(Call, CPU)；Time 仅参考，不参与暂停`;
  }
  return updated;
}

function defaultTimeThresholds(thresholds) {
  const fallback = 150;
  const t = Number(thresholds?.buc_total_time ?? fallback);
  return { warn: t, pause: t, hard_stop: t };
}

function UsageBlock({ title, usage, thresholds, timeThresholds, appMode = false }) {
  const raw = usage || ZERO_USAGE;
  const data = appMode
    ? { ...raw, peak_pct: appGatePeak(raw) }
    : raw;
  const level = levelKey(data.level);
  const tip = buildUsageTip(data, { appMode });
  const timeThr = timeThresholds || thresholds;

  return (
    <div style={{ minWidth: 140 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginBottom: 4 }}>
        <span style={{ fontSize: 11, fontWeight: 600, color: '#555' }}>{title}</span>
        <Tag color={LEVEL_COLORS[level]} style={{ margin: 0, fontSize: 10, lineHeight: '16px' }}>
          {LEVEL_LABELS[level] || level}
        </Tag>
      </div>
      <Tooltip title={tip}>
        <div>
          <MiniBar label="峰值" pct={data.peak_pct} thresholds={thresholds} />
          <MiniBar label="Call" pct={data.call_pct} thresholds={thresholds} />
          <MiniBar label="CPU" pct={data.cpu_pct} thresholds={thresholds} />
          <MiniBar label="Time" pct={data.time_pct} thresholds={timeThr} />
        </div>
      </Tooltip>
    </div>
  );
}

/**
 * @param {object} props
 * @param {object} [props.fbUsage] - 后端 fb_usage 字段
 * @param {'app'|'developer'|'account'} [props.mode]
 * @param {object} [props.thresholds]
 */
const FbUsageCell = ({ fbUsage, mode = 'app', thresholds: thresholdsProp }) => {
  const thresholds = thresholdsProp || fbUsage?.thresholds;
  const timeThresholds = defaultTimeThresholds(thresholds);

  if (mode === 'account') {
    return (
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
        <UsageBlock
          title="X-Ad-Account"
          usage={fbUsage?.x_ad_account_usage}
          thresholds={thresholds}
        />
        <UsageBlock
          title="X-BUC"
          usage={fbUsage?.x_business_use_case_usage}
          thresholds={thresholds}
          timeThresholds={timeThresholds}
        />
      </div>
    );
  }

  return (
    <UsageBlock
      title="X-App-Usage"
      usage={fbUsage?.x_app_usage}
      thresholds={thresholds}
      appMode
    />
  );
};

export default FbUsageCell;
